import React, { useCallback, useEffect, useState, useContext, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "../utils/toast";
import { motion, AnimatePresence } from "framer-motion";
import { MdVerifiedUser, MdClose, MdOutlineInventory2 } from "react-icons/md";
import {
  FaStar,
  FaRegHeart,
  FaHeart,
  FaBalanceScale,
  FaTruck,
  FaQrcode,
  FaAngleRight,
  FaPlus,
  FaMinus,
  FaRegUserCircle,
  FaCheckCircle,
  FaShieldAlt,
  FaShoppingCart,
  FaGlobe,
  FaBoxOpen,
  FaTag,
} from "react-icons/fa";
import { BsLightningChargeFill, BsTagsFill } from "react-icons/bs";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { getBulkPackagingKey, isBulkPackagingKey } from "../utils/productUtils";

import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import ProductNotFound from "../components/ProductNotFound";
import https from "../utils/https";

import "../styles/DynamicProductDetails.css";
import ProductCard from "../components/home/ProductCard";
import {
  isInWishlist as checkWishlist,
  toggleWishlist,
} from "../utils/wishlistSync";
import { AuthContext } from "../context/AuthContext";
import { fetchCartItems } from "../utils/cartSync";

const displayINRCurrency = (num: number) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
};

const DynamicProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuth } = useContext(AuthContext);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);

  const [activeImage, setActiveImage] = useState("");
  const [zoomImageCoordinate, setZoomImageCoordinate] = useState({
    x: 0,
    y: 0,
  });
  const [zoomImage, setZoomImage] = useState(false);

  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [localQuantity, setLocalQuantity] = useState(1);
  const [purchaseMode, setPurchaseMode] = useState<"coil" | "box">("coil");

  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [expandedReviews, setExpandedReviews] = useState(false);

  const cleanId =
    data?._id && String(data._id) !== "undefined"
      ? String(data._id)
      : data?.id && String(data.id) !== "undefined"
        ? String(data.id)
        : null;
  const [isInWishlist, setIsInWishlist] = useState(
    checkWishlist(cleanId || undefined),
  );

  useEffect(() => {
    setIsInWishlist(checkWishlist(cleanId || undefined));

    const handleWishlistUpdate = () => {
      setIsInWishlist(checkWishlist(cleanId || undefined));
    };

    window.addEventListener("wishlistUpdated", handleWishlistUpdate);
    return () =>
      window.removeEventListener("wishlistUpdated", handleWishlistUpdate);
  }, [cleanId]);

  const handleToggleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!data || !cleanId) return;

    const productObj = {
      _id: cleanId,
      name: data.name,
      price: data.price,
      images:
        data.images?.map((url: string) => ({ url })) ||
        (data.image ? [{ url: data.image }] : []),
    };
    await toggleWishlist(productObj);
  };

  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [isReviewsDrawerOpen, setIsReviewsDrawerOpen] = useState(false);
  const [hasClosedReviewPopup, setHasClosedReviewPopup] = useState(false);

  const [isDetailsMobile, setIsDetailsMobile] = useState(false);

  const contextName = data?.category?.name || data?.subcategory?.name || data?.main_category?.name || data?.name || "";
  const rollsSpecKey = getBulkPackagingKey(data?.unit || "", contextName);
  const rollsSpec = data?.specifications?.find((s: any) => isBulkPackagingKey(s.key))?.value;
  const weightSpec = data?.specifications?.find(
    (s: any) =>
      s.key?.trim() === "Weight/Coil" || s.key?.trim() === "Weight / Coil",
  )?.value;

  const rollsCount = rollsSpec
    ? parseInt(String(rollsSpec).match(/\d+/)?.[0] || "0", 10)
    : 0;
  const weightVal = weightSpec
    ? parseFloat(String(weightSpec).match(/[\d.]+/)?.[0] || "0")
    : 0;

  // Helper to extract single price
  const getSingleSellingPrice = (source: any) => {
    if (!source) return 0;
    if (Array.isArray(source.price)) {
      const match = source.price.find((p: any) => p && (p.type === "single" || p.type === "coil"));
      return match ? match.price : (source.price[0]?.price || 0);
    }
    return typeof source.price === "number" ? source.price : (Number(source.price) || 0);
  };

  const getSingleMRP = (source: any) => {
    if (!source) return 0;
    if (Array.isArray(source.price)) {
      const match = source.price.find((p: any) => p && (p.type === "single" || p.type === "coil"));
      return match && typeof match.mrp === "number" ? match.mrp : getSingleSellingPrice(source);
    }
    return source.mrp || source.price || 0;
  };

  // Helper to extract box price
  const getBoxSellingPrice = (source: any, rolls: number) => {
    if (!source) return 0;
    if (Array.isArray(source.price)) {
      const match = source.price.find((p: any) => p && p.type === "box");
      if (match) return match.price;
    }
    return source.box_price || (getSingleSellingPrice(source) * rolls);
  };

  const getBoxMRP = (source: any, rolls: number) => {
    if (!source) return 0;
    if (Array.isArray(source.price)) {
      const match = source.price.find((p: any) => p && p.type === "box");
      if (match && typeof match.mrp === "number") return match.mrp;
    }
    return source.box_mrp || (getSingleMRP(source) * rolls);
  };

  const priceSource = selectedVariant || data;
  const singleSellingPrice = getSingleSellingPrice(priceSource);
  const singleMRP = getSingleMRP(priceSource);
  const boxSellingPrice = getBoxSellingPrice(priceSource, rollsCount);
  const boxMRP = getBoxMRP(priceSource, rollsCount);

  const singleDiscount =
    singleMRP > singleSellingPrice
      ? Math.round(((singleMRP - singleSellingPrice) / singleMRP) * 100)
      : 0;

  const boxDiscount =
    boxMRP > boxSellingPrice
      ? Math.round(((boxMRP - boxSellingPrice) / boxMRP) * 100)
      : 0;

  const hasBoxOption =
    rollsCount > 0 ||
    (Array.isArray(priceSource?.price) && priceSource.price.some((p: any) => p && p.type === "box"));

  useEffect(() => {
    const handleResize = () => {
      const isTab = window.innerWidth <= 1024;
      setIsDetailsMobile(isTab);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [similarProducts, setSimilarProducts] = useState<any[]>([]);
  const [likedProducts, setLikedProducts] = useState<any[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const similarScrollRef = React.useRef<HTMLDivElement>(null);
  const reviewsSectionRef = React.useRef<HTMLDivElement>(null);
  const reviewTimeoutRef = React.useRef<any>(null);

  const [apiReviews, setApiReviews] = useState<any[]>([]);
  const [ratingSummary, setRatingSummary] = useState<any>(null);

  const fetchApiReviews = useCallback(async () => {
    if (!id) return;
    try {
      const res: any = await https.get(`reviews?filter=${encodeURIComponent(JSON.stringify({ product_id: id }))}`);
      const revs = res.data || (Array.isArray(res) ? res : []);
      setApiReviews(revs);
    } catch (err) {
      console.error("Failed to fetch product reviews:", err);
    }

    try {
      const summaryRes: any = await https.get(`reviews/product/${id}/summary`);
      if (summaryRes) setRatingSummary(summaryRes);
    } catch (e) {}
  }, [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setShowReviewPopup(false);
    setIsReviewsDrawerOpen(false);
    fetchProductDetails();
    fetchSimilarProducts();
    fetchApiReviews();
  }, [id, fetchApiReviews]);

  const displayReviews = useMemo(() => {
    if (apiReviews && apiReviews.length > 0) {
      return apiReviews.map((item: any) => {
        let name = "Customer";
        if (item.user_id && typeof item.user_id === "object") {
          name = `${item.user_id.first_name || ""} ${item.user_id.last_name || ""}`.trim() || item.user_id.email || "Customer";
        } else if (item.userInfo) {
          if (typeof item.userInfo === "string") {
            try {
              const parsed = JSON.parse(item.userInfo);
              name = parsed.fullname || parsed.name || "Customer";
            } catch (e) {
              name = item.userInfo;
            }
          } else if (typeof item.userInfo === "object") {
            name = item.userInfo.fullname || item.userInfo.name || "Customer";
          }
        }

        const dateStr = item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
          : "Recently";

        return {
          id: item._id,
          user: name,
          rating: item.rating || 5,
          date: dateStr,
          comment: item.comment || "Great quality product!",
          images: item.images || [],
        };
      });
    }
    return [];
  }, [apiReviews]);

  const displaySummary = useMemo(() => {
    if (ratingSummary && ratingSummary.total_reviews > 0) {
      const distMap = new Map((ratingSummary.distribution || []).map((d: any) => [d.star, d.percentage]));
      return {
        avg: ratingSummary.average_rating || 5.0,
        total: ratingSummary.total_reviews,
        bars: [
          { s: 5, p: distMap.get(5) || 0, color: "#43c8ca" },
          { s: 4, p: distMap.get(4) || 0, color: "#84cc16" },
          { s: 3, p: distMap.get(3) || 0, color: "#facc15" },
          { s: 2, p: distMap.get(2) || 0, color: "#fb923c" },
          { s: 1, p: distMap.get(1) || 0, color: "#f87171" },
        ],
      };
    }
    if (apiReviews && apiReviews.length > 0) {
      const avg = (apiReviews.reduce((acc, r) => acc + (r.rating || 5), 0) / apiReviews.length).toFixed(1);
      return {
        avg: Number(avg),
        total: apiReviews.length,
        bars: [5, 4, 3, 2, 1].map(star => {
          const count = apiReviews.filter(r => (r.rating || 5) === star).length;
          const p = Math.round((count / apiReviews.length) * 100);
          const colorMap: Record<number, string> = { 5: "#43c8ca", 4: "#84cc16", 3: "#facc15", 2: "#fb923c", 1: "#f87171" };
          return { s: star, p, color: colorMap[star] };
        }),
      };
    }
    return null;
  }, [ratingSummary, apiReviews]);

  const [isReviewsIntersecting, setIsReviewsIntersecting] = useState(false);
  const [isFooterIntersecting, setIsFooterIntersecting] = useState(false);
  const footerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = reviewsSectionRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsReviewsIntersecting(entry.isIntersecting);
      },
      { threshold: 0.1 },
    );

    observer.observe(currentRef);
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  useEffect(() => {
    const currentFooter = footerRef.current;
    if (!currentFooter) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFooterIntersecting(entry.isIntersecting);
      },
      { threshold: 0.05 },
    );

    observer.observe(currentFooter);
    return () => {
      if (currentFooter) {
        observer.unobserve(currentFooter);
      }
    };
  }, []);

  useEffect(() => {
    const isMobileScreen = window.innerWidth <= 1024;
    if (isMobileScreen && hasClosedReviewPopup) {
      setShowReviewPopup(false);
      return;
    }

    if (isReviewsIntersecting && !isFooterIntersecting) {
      if (!(isMobileScreen && hasClosedReviewPopup)) {
        setShowReviewPopup(true);
      }
    } else {
      setShowReviewPopup(false);
    }
  }, [isReviewsIntersecting, isFooterIntersecting, hasClosedReviewPopup]);

  useEffect(() => {
    const isMobileScreen = window.innerWidth <= 1024;
    if (showReviewPopup && isMobileScreen) {
      reviewTimeoutRef.current = setTimeout(() => {
        setShowReviewPopup(false);
        setHasClosedReviewPopup(true);
      }, 7000);
    }
    return () => {
      if (reviewTimeoutRef.current) {
        clearTimeout(reviewTimeoutRef.current);
      }
    };
  }, [showReviewPopup]);

  const fetchSimilarProducts = async () => {
    try {
      setLoadingSimilar(true);
      const res = await https.get("products");
      let allProducts = [];
      if (res.success && Array.isArray(res.data)) {
        allProducts = res.data;
      } else if (res.data && res.data.data && Array.isArray(res.data.data)) {
        allProducts = res.data.data;
      }

      const filtered = allProducts.filter((p: any) => (p._id || p.id) !== id);
      setSimilarProducts(filtered.slice(0, 4));
      setLikedProducts(filtered.slice(4, 12));
    } catch (error) {
      console.error("Error fetching similar products:", error);
    } finally {
      setLoadingSimilar(false);
    }
  };

  const scroll = (
    direction: "left" | "right",
    type: "similar" | "liked" = "liked",
  ) => {
    const ref = type === "similar" ? similarScrollRef : scrollRef;
    if (ref.current) {
      const { scrollLeft, clientWidth } = ref.current;
      const scrollTo =
        direction === "left"
          ? scrollLeft - clientWidth
          : scrollLeft + clientWidth;
      ref.current.scrollTo({ left: scrollTo, behavior: "smooth" });
    }
  };

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const res = await https.get(`products/${id}`);
      if (res.success && res.data) {
        const prod = res.data;
        setData(prod);

        const imgs =
          prod.images && prod.images.length > 0
            ? prod.images
            : ["/images/products/placeholder.png"];
        setActiveImage(imgs[0]);

        if (prod.variants && prod.variants.length > 0) {
          setSelectedVariant(prod.variants[0]);
        }
      } else {
        setData(null);
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const [isAdded, setIsAdded] = useState(false);

  useEffect(() => {
    const cleanId =
      data?._id && String(data._id) !== "undefined"
        ? String(data._id)
        : data?.id && String(data?.id) !== "undefined"
          ? String(data?.id)
          : id || null;
    if (!cleanId) return;

    const handleCartUpdate = () => {
      const currentCartStr = localStorage.getItem("honeywill_cart");
      const cartList: any[] = currentCartStr ? JSON.parse(currentCartStr) : [];
      const targetCartId = `${cleanId}_${purchaseMode}`;
      const foundItem = cartList.find((item) => item.id === targetCartId);
      setIsAdded(!!foundItem);
      if (foundItem) {
        setLocalQuantity(foundItem.no || 1);
      } else if (!isAdded) {
        // Only reset if it was previously added and now removed
        setLocalQuantity(1);
      }
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    handleCartUpdate();

    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
    };
  }, [data, id, purchaseMode]);
  const handleAddToCart = async () => {
    if (!data) return;
    try {
      setAddingToCart(true);
      const cleanId =
        data._id && String(data._id) !== "undefined"
          ? String(data._id)
          : data.id && String(data.id) !== "undefined"
            ? String(data.id)
            : null;
      if (!cleanId) {
        console.error(
          "DynamicProductDetails: Could not resolve a valid product ID. Product details:",
          data,
        );
        toast.warning("Cannot add this product because its ID is invalid.");
        return;
      }

      if (isAuth) {
        // Send directly to the API
        await https.post("cart/add", {
          product_id: cleanId,
          quantity: purchaseMode === "box" ? 1 : localQuantity,
          purchase_mode: purchaseMode,
        });
        // Retrieve the single source of truth from backend and sync localStorage
        await fetchCartItems();
        window.dispatchEvent(new Event("cartUpdated"));
      } else {
        const parsedPrice = purchaseMode === "box" ? boxSellingPrice : singleSellingPrice;
        const parsedOriginalPrice = purchaseMode === "box" ? boxMRP : singleMRP;
        const fallbackImage = "/images/products/placeholder.png";
        const imageUrl =
          selectedVariant?.image?.url ||
          data.images?.[0] ||
          data.image ||
          fallbackImage;

        const cartItem = {
          id: `${cleanId}_${purchaseMode}`,
          name: purchaseMode === "box" ? `${data.name} (${rollsSpecKey})` : `${data.name} (${data?.unit || 'Piece'})`,
          image: imageUrl,
          ram: selectedVariant?.name || "Standard",
          subtotal: parsedPrice * (purchaseMode === "box" ? 1 : localQuantity),
          cuttoff: `₹${Math.ceil(parsedOriginalPrice).toLocaleString()}`,
          no: purchaseMode === "box" ? 1 : localQuantity,
          priceA: parsedPrice,
          purchaseMode: purchaseMode,
          productId: cleanId,
        };

        const currentCartStr = localStorage.getItem("honeywill_cart");
        const cartList: any[] = currentCartStr ? JSON.parse(currentCartStr) : [];

        const existingIndex = cartList.findIndex(
          (item) => item.id === cartItem.id,
        );
        if (existingIndex > -1) {
          if (purchaseMode === "box") {
            cartList[existingIndex].no = 1;
          } else {
            cartList[existingIndex].no += localQuantity;
          }
          cartList[existingIndex].subtotal =
            cartList[existingIndex].no * cartList[existingIndex].priceA;
        } else {
          cartList.push(cartItem);
        }

        localStorage.setItem("honeywill_cart", JSON.stringify(cartList));
        window.dispatchEvent(new Event("cartUpdated"));
      }

      toast.success("Product added to cart successfully!");
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add product to cart");
    } finally {
      setAddingToCart(false);
    }
  };

  const handleUpdateQuantity = async (delta: number) => {
    const newQty = localQuantity + delta;
    if (newQty < 1) return;
    
    // Optimistically update UI
    setLocalQuantity(newQty);

    const targetCartId = `${cleanId}_${purchaseMode}`;
    
    if (isAuth) {
      try {
        await https.post("cart/update-quantity", {
          product_id: cleanId,
          quantity: newQty,
          purchase_mode: purchaseMode,
        });
        await fetchCartItems();
        window.dispatchEvent(new Event("cartUpdated"));
      } catch (err) {
        console.error("Failed to update quantity:", err);
      }
    } else {
      const currentCartStr = localStorage.getItem("honeywill_cart");
      const cartList: any[] = currentCartStr ? JSON.parse(currentCartStr) : [];
      const existingIndex = cartList.findIndex((item) => item.id === targetCartId);
      if (existingIndex > -1) {
        cartList[existingIndex].no = newQty;
        cartList[existingIndex].subtotal = cartList[existingIndex].no * cartList[existingIndex].priceA;
        localStorage.setItem("honeywill_cart", JSON.stringify(cartList));
        window.dispatchEvent(new Event("cartUpdated"));
      }
    }
  };

  const handleBuyNow = async () => {
    if (!data) return;
    try {
      const cleanId =
        data._id && String(data._id) !== "undefined"
          ? String(data._id)
          : data.id && String(data.id) !== "undefined"
            ? String(data.id)
            : null;
      if (!cleanId) {
        console.error(
          "DynamicProductDetails: Could not resolve a valid product ID. Product details:",
          data,
        );
        toast.warning("Cannot proceed because this product's ID is invalid.");
        return;
      }

      if (isAuth) {
        // Send directly to the API
        await https.post("cart/add", {
          product_id: cleanId,
          quantity: purchaseMode === "box" ? 1 : localQuantity,
          purchase_mode: purchaseMode,
        });
        // Retrieve the single source of truth from backend and sync localStorage
        await fetchCartItems();
        window.dispatchEvent(new Event("cartUpdated"));
      } else {
        const parsedPrice = purchaseMode === "box" ? boxSellingPrice : singleSellingPrice;
        const parsedOriginalPrice = purchaseMode === "box" ? boxMRP : singleMRP;
        const fallbackImage = "/images/products/placeholder.png";
        const imageUrl =
          selectedVariant?.image?.url ||
          data.images?.[0] ||
          data.image ||
          fallbackImage;

        const cartItem = {
          id: `${cleanId}_${purchaseMode}`,
          name: purchaseMode === "box" ? `${data.name} (${rollsSpecKey})` : `${data.name} (${data?.unit || 'Piece'})`,
          image: imageUrl,
          ram: selectedVariant?.name || "Standard",
          subtotal: parsedPrice * (purchaseMode === "box" ? 1 : localQuantity),
          cuttoff: `₹${Math.ceil(parsedOriginalPrice).toLocaleString()}`,
          no: purchaseMode === "box" ? 1 : localQuantity,
          priceA: parsedPrice,
          purchaseMode: purchaseMode,
          productId: cleanId,
        };

        const currentCartStr = localStorage.getItem("honeywill_cart");
        const cartList: any[] = currentCartStr ? JSON.parse(currentCartStr) : [];

        const existingIndex = cartList.findIndex(
          (item) => item.id === cartItem.id,
        );
        if (existingIndex > -1) {
          if (purchaseMode === "box") {
            cartList[existingIndex].no = 1;
          } else {
            cartList[existingIndex].no += localQuantity;
          }
          cartList[existingIndex].subtotal =
            cartList[existingIndex].no * cartList[existingIndex].priceA;
        } else {
          cartList.push(cartItem);
        }

        localStorage.setItem("honeywill_cart", JSON.stringify(cartList));
        window.dispatchEvent(new Event("cartUpdated"));
      }

      navigate("/cart?mode=checkout");
    } catch (error) {
      console.error("Error with buy now:", error);
      toast.error("Failed to proceed with purchase");
    }
  };

  const handleMouseEnterProduct = (imageURL: string) => {
    setActiveImage(imageURL);
  };

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  if (!data && !loading) {
    return (
      <div className="product-details-container">
        <Header />
        <ProductNotFound />
        <Footer />
      </div>
    );
  }

  const currentSellingPrice = singleSellingPrice;
  const currentMRP = singleMRP;
  const currentDiscount = singleDiscount;
  const currentStock = selectedVariant
    ? selectedVariant.stock
    : data?.stock !== undefined
      ? data.stock
      : 99;
  const currentSku = selectedVariant
    ? selectedVariant.sku
    : data?.sku || data?._id || data?.id;
  const isAvailable = selectedVariant
    ? selectedVariant.isAvailable
    : data?.isAvailable !== undefined
      ? data.isAvailable
      : true;

  const variantsGroupKey =
    data?.variants && data.variants.length > 0 && data.variants[0].color
      ? "color"
      : data?.variants &&
        data.variants.length > 0 &&
        data.variants[0].attribute_name
        ? "attribute_name"
        : "DefaultGroup";

  const variantsByGroup = data?.variants
    ? data.variants.reduce((acc: any, variant: any) => {
      const groupVal =
        variant[variantsGroupKey] || variant.color || "Default";
      if (!acc[groupVal]) acc[groupVal] = [];
      acc[groupVal].push(variant);
      return acc;
    }, {})
    : {};

  const imagesList = data?.images?.length > 0 ? data.images : [activeImage];

  const faqs = [
    {
      question: "How to return this product?",
      answer:
        "You can return it within 7 days of delivery. Make sure the tags are intact.",
    },
    {
      question: "Is this product genuine?",
      answer:
        "Yes, we source all our products directly from the brands or authorized distributors.",
    },
  ];

  const reviews = [
    {
      id: 1,
      user: "Anurag",
      rating: 5,
      comment:
        "The ownership transfer process at HoneyCool was smooth, well-structured, and efficient. Clear documentation, timely knowledge transfer sessions, and proactive support from both teams ensured a seamless handover with zero disruption.",
      date: "18 May 2026",
    },
    {
      id: 2,
      user: "Shubham Katiyar",
      rating: 5,
      comment:
        "I've had a great experience with HoneyCool and would definitely recommend their services to anyone looking to rent furniture or appliances. From placing the order to delivery and installation, everything was seamless and professional.",
      date: "15 May 2026",
    },
    {
      id: 3,
      user: "Shubham Chaudhary",
      rating: 5,
      comment: "very good",
      date: "12 May 2026",
    },
    {
      id: 4,
      user: "Deshpande Onkar",
      rating: 5,
      comment:
        "Impressed with HoneyCool's next-day delivery! The process was smooth and super quick. Got my furniture delivered the very next day as promised. Efficient, reliable, and perfect for urgent needs!",
      date: "10 May 2026",
    },
    {
      id: 5,
      user: "Ilavarasan A",
      rating: 5,
      comment: "Excellent service's",
      date: "08 May 2026",
    },
    {
      id: 6,
      user: "Ayushee Manhas",
      rating: 5,
      comment: "Best",
      date: "05 May 2026",
    },
    {
      id: 7,
      user: "Anurag",
      rating: 5,
      comment: "Smooth closure",
      date: "03 May 2026",
    },
    {
      id: 8,
      user: "Balwant Rautela",
      rating: 5,
      comment: "excellent",
      date: "01 May 2026",
    },
    {
      id: 9,
      user: "Thai Kavya Swaroopa",
      rating: 5,
      comment: "Good",
      date: "28 Apr 2026",
    },
    {
      id: 10,
      user: "Aakash",
      rating: 5,
      comment: "excellent purchase experience",
      date: "25 Apr 2026",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="product-details-container"
    >
      <Header />

      <div className="product-details-wrapper">
        <div
          className="details-nav-bar"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: "#fff",
              color: "#1e293b",
              border: "1px solid #cbd5e1",
              padding: "8px 18px",
              borderRadius: "30px",
              fontWeight: "700",
              fontSize: "13px",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#43c8ca";
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.borderColor = "#43c8ca";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#fff";
              e.currentTarget.style.color = "#1e293b";
              e.currentTarget.style.borderColor = "#cbd5e1";
            }}
          >
            <BiChevronLeft size={22} />
            <span>Back to Products</span>
          </button>

          <div className="breadcrumb" style={{ margin: 0 }}>
            <Link to="/">Home</Link>
            <FaAngleRight size={10} />
            <Link to="/products">Products</Link>
            <FaAngleRight size={10} />
            <span className="active">{data?.name}</span>
          </div>
        </div>

        <div className="product-main-card">
          <div className="product-content-grid">
            {/* LEFT: IMAGE SECTION */}
            <div className="product-left-col">
              <div className="main-image-container">
                <div className="wishlist-btn-container">
                  <button
                    onClick={handleToggleWishlist}
                    className={`wishlist-btn ${isInWishlist ? "active" : ""}`}
                  >
                    {isInWishlist ? (
                      <FaHeart size={20} />
                    ) : (
                      <FaRegHeart size={20} />
                    )}
                  </button>
                </div>

                {loading ? (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#f8fafc",
                      borderRadius: "0px",
                      animation:
                        "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                    }}
                  ></div>
                ) : (
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      borderRadius: "0px",
                    }}
                  >
                    <img
                      src={activeImage || "/images/products/placeholder.png"}
                      className="main-image"
                      alt={data?.name}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "/images/products/placeholder.png";
                      }}
                    />
                  </div>
                )}

                <div className="badges-container">
                  {data?.is_featured && (
                    <div className="badge-featured">
                      <FaStar /> FEATURED
                    </div>
                  )}
                  {data?.is_new && (
                    <div className="badge-new">
                      <BsTagsFill /> NEW ARRIVAL
                    </div>
                  )}
                </div>
              </div>

              <div className="thumbnails-container">
                {loading
                  ? [1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      style={{
                        minWidth: "80px",
                        height: "6rem",
                        backgroundColor: "#f8fafc",
                        borderRadius: "0px",
                        animation:
                          "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                      }}
                    ></div>
                  ))
                  : imagesList.map((imgURL: string, idx: number) => (
                    <div
                      key={idx}
                      className={`thumbnail-item ${activeImage === imgURL ? "active" : ""}`}
                      onMouseEnter={() => handleMouseEnterProduct(imgURL)}
                    >
                      <img
                        src={imgURL || "/images/products/placeholder.png"}
                        alt="thumbnail"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "/images/products/placeholder.png";
                        }}
                      />
                    </div>
                  ))}
              </div>

              {/* <div className='features-grid'>
                                {[
                                    { icon: <FaBalanceScale />, label: 'Genuine Product' },
                                    { icon: <FaTruck />, label: 'Fast Delivery' },
                                    { icon: <FaQrcode />, label: 'QR Verified' }
                                ].map((item, idx) => (
                                    <div key={idx} className='feature-item'>
                                        <div className='feature-icon-wrapper'>{React.cloneElement(item.icon, { size: 22 })}</div>
                                        <span className='feature-label'>{item.label}</span>
                                    </div>
                                ))}
                            </div> */}
            </div>

            {/* RIGHT: DETAILS SECTION */}
            <div className="product-right-col">
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.5rem",
                    animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                  }}
                >
                  <div
                    style={{
                      height: "2.5rem",
                      backgroundColor: "#f8fafc",
                      width: "75%",
                      borderRadius: "0px",
                    }}
                  ></div>
                  <div
                    style={{
                      height: "4rem",
                      backgroundColor: "#f8fafc",
                      width: "100%",
                      borderRadius: "0px",
                    }}
                  ></div>
                  <div
                    style={{
                      height: "10rem",
                      backgroundColor: "#f8fafc",
                      width: "100%",
                      borderRadius: "0px",
                    }}
                  ></div>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.3rem",
                    }}
                  >
                    {/* Brand & Category Row */}
                    <div
                      className="brand-row"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "0px",
                      }}
                    >
                      <div className="brand-info">
                        <span
                          className="brand-name"
                          style={{
                            backgroundColor: "#f1f5f9",
                            padding: "6px 16px",
                            fontSize: "12px",
                            fontWeight: "800",
                            letterSpacing: "1px",
                            borderRadius: "4px",
                          }}
                        >
                          {data?.brand_id?.name || "Brand"}
                        </span>
                      </div>
                      <span
                        className="category-badge"
                        style={{
                          backgroundColor: "#e6f9f9",
                          color: "#43c8ca",
                          fontSize: "11px",
                          fontWeight: "800",
                          padding: "6px 16px",
                          letterSpacing: "1px",
                          borderRadius: "4px",
                        }}
                      >
                        {data?.subcategory_id?.name || data?.category_id?.name}
                      </span>
                    </div>

                    {/* Product Title */}
                    <h1
                      className="product-title"
                      style={{
                        fontSize: "38px",
                        lineHeight: "1.25",
                        color: "#0f172a",
                        fontWeight: "900",
                        textAlign: "left",
                        letterSpacing: "-0.5px",
                      }}
                    >
                      {data?.name}
                    </h1>

                    {/* Rating & Origin Row */}
                    <div
                      className="product-meta-row"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        paddingBottom: "6px",
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        className="rating-badge"
                        style={{
                          backgroundColor: "#fef08a",
                          color: "#854d0e",
                          borderRadius: "4px",
                          padding: "6px 14px",
                          fontSize: "16px",
                          fontWeight: "800",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        4.5 <FaStar size={14} style={{ color: "#854d0e" }} />
                      </div>
                      <span
                        className="rating-text"
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        98 Verified Ratings
                      </span>
                      <div
                        style={{
                          width: "1px",
                          height: "18px",
                          backgroundColor: "#e2e8f0",
                        }}
                      ></div>
                      <span
                        className="origin-text"
                        style={{
                          fontSize: "14px",
                          color: "#64748b",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <FaGlobe style={{ color: "#43c8ca" }} />{" "}
                        {data?.countryOfOrigin || "India"}
                      </span>
                    </div>
                  </div>

                  <div
                    className="product-description-container"
                    style={{ textAlign: "left", margin: "4px 0 2px 0" }}
                  >
                    <p
                      style={{
                        color: "#475569",
                        fontWeight: "500",
                        fontSize: "16px",
                        lineHeight: "1.7",
                        margin: 0,
                      }}
                    >
                      {data?.description}
                    </p>
                  </div>

                  {/* Pricing Section */}
                  <div className="pricing-section">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: hasBoxOption ? "1fr 1fr" : "1fr",
                        gap: "10px",
                        margin: "12px 0",
                      }}
                    >
                      {/* Box 1: Coil Price */}
                      <div
                        className="price-showcase-card"
                        onClick={() => {
                          setPurchaseMode("coil");
                          setLocalQuantity(1);
                        }}
                        style={{
                          backgroundColor:
                            purchaseMode === "coil" ? "#f8fafc" : "#ffffff",
                          border:
                            purchaseMode === "coil"
                              ? "2px solid #0d9488"
                              : "1px solid #e2e8f0",
                          borderRadius: "8px",
                          padding: "10px 14px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "11px",
                            fontWeight: "800",
                            color:
                              purchaseMode === "coil" ? "#0d9488" : "#64748b",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Price / {data?.unit ? data.unit : ""}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "22px",
                              fontWeight: "800",
                              color: "#0f172a",
                              letterSpacing: "-0.5px",
                              lineHeight: "1",
                            }}
                          >
                            {displayINRCurrency(singleSellingPrice)}
                          </span>
                          <span
                            style={{
                              fontSize: "13px",
                              color: "#94a3b8",
                              textDecoration: "line-through",
                              fontWeight: "600",
                            }}
                          >
                            {displayINRCurrency(singleMRP)}
                          </span>
                          {singleDiscount > 0 && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                background: "#10b981",
                                color: "#ffffff",
                                fontSize: "10px",
                                fontWeight: "800",
                                padding: "2px 6px",
                                borderRadius: "4px",
                              }}
                            >
                              {singleDiscount}% OFF
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: "10px",
                              fontWeight: "700",
                              color: "#64748b",
                              background: "#f1f5f9",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            Incl. 18% GST
                          </span>
                        </div>
                      </div>

                      {/* Box 2: Box Price */}
                      {hasBoxOption && (
                        <div
                          className="price-showcase-card"
                          onClick={() => {
                            setPurchaseMode("box");
                            setLocalQuantity(1);
                          }}
                          style={{
                            backgroundColor:
                              purchaseMode === "box" ? "#f0fdf4" : "#ffffff",
                            border:
                              purchaseMode === "box"
                                ? "2px solid #16a34a"
                                : "1px solid #e2e8f0",
                            borderRadius: "8px",
                            padding: "10px 14px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: "800",
                              color:
                                purchaseMode === "box" ? "#166534" : "#64748b",
                              textTransform: "uppercase",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              letterSpacing: "0.5px",
                            }}
                          >
                            <span>Price / {rollsSpecKey}</span>
                            <span
                              style={{
                                fontSize: "10px",
                                background:
                                  purchaseMode === "box"
                                    ? "#dcfce7"
                                    : "#f1f5f9",
                                padding: "2px 6px",
                                borderRadius: "10px",
                                color:
                                  purchaseMode === "box"
                                    ? "#166534"
                                    : "#64748b",
                              }}
                            >
                              {rollsCount > 0 ? `${rollsCount} ${data?.unit ? data.unit : ""}` : rollsSpecKey}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: "8px",
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "22px",
                                fontWeight: "800",
                                color: "#14532d",
                                letterSpacing: "-0.5px",
                                lineHeight: "1",
                              }}
                            >
                              {displayINRCurrency(boxSellingPrice)}
                            </span>
                            <span
                              style={{
                                fontSize: "13px",
                                color: "#86efac",
                                textDecoration: "line-through",
                                fontWeight: "600",
                              }}
                            >
                              {displayINRCurrency(boxMRP)}
                            </span>
                            {boxDiscount > 0 && (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  background: "#22c55e",
                                  color: "#ffffff",
                                  fontSize: "10px",
                                  fontWeight: "800",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                }}
                              >
                                {boxDiscount}% OFF
                              </span>
                            )}
                            <span
                              style={{
                                fontSize: "10px",
                                fontWeight: "700",
                                color: "#166534",
                                background: "#dcfce7",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                border: "1px solid #bbf7d0",
                              }}
                            >
                              Incl. 18% GST
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Total Price Section */}
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        padding: "12px 16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "800",
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Total Price for {localQuantity}{" "}
                        {purchaseMode === "box"
                          ? localQuantity === 1
                            ? rollsSpecKey
                            : (rollsSpecKey.toLowerCase() === 'box' ? 'Boxes' : `${rollsSpecKey}s`)
                          : localQuantity === 1
                            ? (data?.unit || "Piece")
                            : (data?.unit ? (data.unit.toLowerCase().endsWith('s') ? data.unit : `${data.unit}s`) : "Pieces")}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "28px",
                            fontWeight: "800",
                            color: "#0f172a",
                            letterSpacing: "-0.5px",
                            lineHeight: "1",
                          }}
                        >
                          {displayINRCurrency(
                            (purchaseMode === "box" ? boxSellingPrice : singleSellingPrice) * localQuantity
                          )}
                        </span>
                        {(purchaseMode === "box" ? boxDiscount : singleDiscount) > 0 && (
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: "700",
                              color: "#065f46",
                              background: "#ecfdf5",
                              padding: "4px 10px",
                              borderRadius: "10px",
                              border: "1px solid #a7f3d0",
                            }}
                          >
                            You save{" "}
                            {displayINRCurrency(
                              ((purchaseMode === "box" ? boxMRP : singleMRP) -
                                (purchaseMode === "box" ? boxSellingPrice : singleSellingPrice)) *
                                localQuantity
                            )}
                          </span>
                        )}
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: "800",
                            color: "#0f766e",
                            background: "#ccfbf1",
                            padding: "6px 12px",
                            borderRadius: "12px",
                            border: "1px solid #99f6e4",
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          Incl. 18% GST
                        </span>
                      </div>

                      {/* ACTION BUTTONS */}
                      <div
                        className="actions-row"
                        style={{
                          display: "flex",
                          gap: "16px",
                          borderTop: "1px solid #e2e8f0",
                          paddingTop: "20px",
                          marginTop: "10px",
                        }}
                      >
                        {isAdded ? (
                          <>
                            <div
                              style={{ 
                                flex: 1,
                                height: "60px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                borderRadius: "6px",
                                border: "2px solid #01bdc0",
                                backgroundColor: "#f0fdfa",
                                padding: "0 10px",
                              }}
                            >
                              <button
                                onClick={() => handleUpdateQuantity(-1)}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "none",
                                  background: "#01bdc0",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  transition: "background-color 0.2s ease"
                                }}
                                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#019ba0")}
                                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#01bdc0")}
                              >
                                <FaMinus size={14} />
                              </button>
                              <span style={{ fontSize: "18px", fontWeight: "bold", color: "#01bdc0" }}>
                                {localQuantity}
                              </span>
                              <button
                                onClick={() => handleUpdateQuantity(1)}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "none",
                                  background: "#01bdc0",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  transition: "background-color 0.2s ease"
                                }}
                                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#019ba0")}
                                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#01bdc0")}
                              >
                                <FaPlus size={14} />
                              </button>
                            </div>
                            <button
                              onClick={() => navigate("/cart")}
                              className="action-btn btn-go-to-bag"
                              style={{
                                flex: 1,
                                height: "60px",
                                fontSize: "16px",
                                fontWeight: "900",
                                borderRadius: "6px",
                                backgroundColor: "#01bdc0",
                                color: "#fff",
                                border: "none",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                                boxShadow: "0 4px 6px -1px rgba(1, 189, 192, 0.3)",
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = "#019ba0";
                                e.currentTarget.style.transform = "translateY(-1px)";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = "#01bdc0";
                                e.currentTarget.style.transform = "translateY(0)";
                              }}
                            >
                              <FaShoppingCart size={18} /> GO TO BAG
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={handleAddToCart}
                              disabled={addingToCart}
                              className="action-btn btn-add-cart"
                              style={{
                                flex: 1,
                                height: "60px",
                                fontSize: "18px",
                                fontWeight: "900",
                                borderRadius: "6px",
                                backgroundColor: "#0f172a",
                                color: "#fff",
                                border: "none",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "10px",
                              }}
                            >
                              <span className="action-btn-content">
                                {addingToCart ? (
                                  "Adding..."
                                ) : (
                                  <>
                                    <FaShoppingCart size={20} /> ADD TO CART
                                  </>
                                )}
                              </span>
                            </button>

                            <button
                              onClick={handleBuyNow}
                              className="action-btn btn-buy-now"
                              style={{
                                flex: 1,
                                height: "60px",
                                fontSize: "18px",
                                fontWeight: "900",
                                borderRadius: "6px",
                                backgroundColor: "#43c8ca",
                                color: "#fff",
                                border: "none",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "10px",
                              }}
                            >
                              <span className="action-btn-content">
                                <BsLightningChargeFill size={20} /> BUY NOW
                              </span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {Object.keys(variantsByGroup).length > 0 && (
                    <div
                      className="variants-container"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "24px",
                        backgroundColor: "#f8fafc",
                        padding: "24px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        margin: "20px 0",
                      }}
                    >
                      {/* Group Selection (e.g. Color) */}
                      {variantsByGroup[Object.keys(variantsByGroup)[0]][0][
                        variantsGroupKey
                      ] &&
                        variantsGroupKey !== "DefaultGroup" && (
                          <div>
                            <div
                              className="variant-header"
                              style={{ marginBottom: "12px" }}
                            >
                              <span
                                className="variant-title"
                                style={{
                                  textTransform: "capitalize",
                                  fontSize: "14px",
                                  fontWeight: "800",
                                  color: "#0f172a",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                {variantsGroupKey}:{" "}
                                <span
                                  style={{
                                    color: "#64748b",
                                    fontWeight: "600",
                                    marginLeft: "4px",
                                  }}
                                >
                                  {selectedVariant?.[variantsGroupKey]}
                                </span>
                              </span>
                            </div>
                            <div
                              className="color-swatch-list"
                              style={{
                                display: "flex",
                                gap: "12px",
                                flexWrap: "wrap",
                              }}
                            >
                              {Object.keys(variantsByGroup).map((groupVal) => {
                                const varObj = variantsByGroup[groupVal][0];
                                const isSelected =
                                  selectedVariant?.[variantsGroupKey] ===
                                  groupVal;

                                if (
                                  variantsGroupKey === "color" ||
                                  varObj.color_hex
                                ) {
                                  return (
                                    <button
                                      key={groupVal}
                                      onClick={() => setSelectedVariant(varObj)}
                                      className={`color-swatch-btn ${isSelected ? "active" : "inactive"}`}
                                      title={groupVal}
                                      style={{
                                        width: "56px",
                                        height: "56px",
                                        borderRadius: "50%",
                                        border: isSelected
                                          ? "3px solid #43c8ca"
                                          : "1px solid #cbd5e1",
                                        padding: "2px",
                                        backgroundColor: "#fff",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        transition: "all 0.2s ease",
                                        transform: isSelected
                                          ? "scale(1.05)"
                                          : "none",
                                      }}
                                    >
                                      <span
                                        className="color-inner"
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          borderRadius: "50%",
                                          backgroundColor:
                                            varObj.color_hex ||
                                            (variantsGroupKey === "color"
                                              ? groupVal
                                              : "#ccc"),
                                        }}
                                      />
                                    </button>
                                  );
                                } else {
                                  return (
                                    <button
                                      key={groupVal}
                                      onClick={() => setSelectedVariant(varObj)}
                                      className={`size-btn ${isSelected ? "active" : "inactive"}`}
                                      style={{
                                        padding: "12px 24px",
                                        borderRadius: "6px",
                                        border: isSelected
                                          ? "2px solid #43c8ca"
                                          : "1px solid #cbd5e1",
                                        backgroundColor: isSelected
                                          ? "#e6f9f9"
                                          : "#fff",
                                        color: isSelected ? "#000" : "#475569",
                                        fontWeight: "800",
                                        fontSize: "15px",
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                      }}
                                    >
                                      {groupVal}
                                    </button>
                                  );
                                }
                              })}
                            </div>
                          </div>
                        )}

                      {/* Secondary Selection (e.g. Size) */}
                      <div>
                        <div
                          className="variant-header"
                          style={{ marginBottom: "12px" }}
                        >
                          <span
                            className="variant-title"
                            style={{
                              fontSize: "14px",
                              fontWeight: "800",
                              color: "#0f172a",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Select Variant
                          </span>
                        </div>
                        <div
                          className="size-grid"
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fill, minmax(120px, 1fr))",
                            gap: "12px",
                          }}
                        >
                          {variantsByGroup[
                            selectedVariant?.[variantsGroupKey] ||
                            Object.keys(variantsByGroup)[0]
                          ]?.map((v: any) => {
                            const isSelected =
                              (selectedVariant?.sku &&
                                selectedVariant?.sku === v.sku) ||
                              (selectedVariant?._id &&
                                selectedVariant?._id === v._id);
                            const isAvail =
                              v.isAvailable !== false && v.stock > 0;
                            return (
                              <button
                                key={v.sku || v._id || Math.random()}
                                onClick={() => setSelectedVariant(v)}
                                disabled={!isAvail}
                                style={{
                                  position: "relative",
                                  padding: "14px 10px",
                                  borderRadius: "6px",
                                  border: isSelected
                                    ? "2px solid #43c8ca"
                                    : "1px solid #cbd5e1",
                                  backgroundColor: isSelected
                                    ? "#e6f9f9"
                                    : isAvail
                                      ? "#fff"
                                      : "#f1f5f9",
                                  color: isSelected
                                    ? "#000"
                                    : isAvail
                                      ? "#475569"
                                      : "#94a3b8",
                                  fontWeight: "800",
                                  fontSize: "15px",
                                  cursor: isAvail ? "pointer" : "not-allowed",
                                  opacity: isAvail ? 1 : 0.6,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s",
                                }}
                              >
                                {v.size || v.name || v.sku || "Select"}
                                {!isAvail && (
                                  <div
                                    className="cross-line"
                                    style={{
                                      position: "absolute",
                                      inset: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "100%",
                                        height: "2px",
                                        backgroundColor: "#cbd5e1",
                                        transform: "rotate(-15deg)",
                                      }}
                                    ></div>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* INTEGRATED DETAILS SECTION (VERTICAL STACKED DESIGN) */}
        <div
          className="modern-details-section"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "40px",
            marginTop: "40px",
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            border: "1px solid #f1f5f9",
            padding: "32px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
          }}
        >
          {/* Description Section */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                borderBottom: "2px solid #cbd5e1",
                paddingBottom: "12px",
              }}
            >
              <FaShieldAlt size={18} style={{ color: "#0f172a" }} />
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "800",
                  color: "#0f172a",
                  margin: 0,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Product Description
              </h3>
            </div>
            <div
              style={{
                color: "#475569",
                fontSize: "15px",
                lineHeight: "1.7",
                textAlign: "left",
              }}
            >
              {data?.description ? (
                data.description
                  .split("\n")
                  .filter((s: string) => s.trim())
                  .map((line: string, idx: number) => (
                    <p key={idx} style={{ marginBottom: "12px" }}>
                      {line.trim()}
                    </p>
                  ))
              ) : (
                <p style={{ color: "#94a3b8", fontStyle: "italic" }}>
                  No detailed description available for this product.
                </p>
              )}
            </div>
          </div>

          {/* Specifications Section (Core and Technical Side-by-Side or Stacked) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: "32px",
            }}
          >
            {/* Core Specifications */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  borderBottom: "2px solid #cbd5e1",
                  paddingBottom: "12px",
                }}
              >
                <FaBoxOpen size={18} style={{ color: "#0f172a" }} />
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "800",
                    color: "#0f172a",
                    margin: 0,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Core Specifications
                </h3>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                {[
                  { label: "Brand", val: data?.brand_id?.name },
                  { label: "Main Category", val: data?.main_category?.name },
                  { label: "Category", val: data?.category_id?.name },
                  {
                    label: "Status",
                    val: ` ${data?.unit || "Units"}`,
                  },
                  {
                    label: "Availability",
                    val: isAvailable ? "In Stock" : "Out of Stock",
                  },
                  {
                    label: "Country of Origin",
                    val: data?.countryOfOrigin || "India",
                  },
                  { label: "Manufacturer", val: data?.manufacturer },
                  {
                    label: "Manufacturer Address",
                    val: data?.manufacturerAddress,
                  },
                  {
                    label: rollsSpecKey,
                    val: data?.specifications?.find((s: any) =>
                      isBulkPackagingKey(s.key),
                    )?.value,
                  },
                  {
                    label: "Weight / Coil",
                    val: weightVal > 0 ? `${weightVal} kg` : null,
                  },
                  {
                    label: "Net Weight/Box",
                    val:
                      weightVal > 0 && rollsCount > 0
                        ? `${Number((weightVal * rollsCount).toFixed(3))} kg`
                        : null,
                  },
                ]
                  .filter((item) => item.val)
                  .map((item, idx, arr) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "12px 16px",
                        backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                        borderBottom:
                          idx === arr.length - 1 ? "none" : "1px solid #e2e8f0",
                        fontSize: "14px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#64748b",
                          textAlign: "left",
                        }}
                      >
                        {item.label}
                      </span>
                      <span
                        style={{
                          fontWeight: "700",
                          color: "#0f172a",
                          textAlign: "right",
                        }}
                      >
                        {item.val}
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Technical Specifications */}
            {data?.specifications && data.specifications.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    borderBottom: "2px solid #cbd5e1",
                    paddingBottom: "12px",
                  }}
                >
                  <FaBalanceScale size={18} style={{ color: "#0f172a" }} />
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "800",
                      color: "#0f172a",
                      margin: 0,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Technical Specifications
                  </h3>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                >
                  {data.specifications.map((spec: any, idx: number) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "12px 16px",
                        backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                        borderBottom:
                          idx === data.specifications.length - 1
                            ? "none"
                            : "1px solid #e2e8f0",
                        fontSize: "14px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#64748b",
                          textAlign: "left",
                        }}
                      >
                        {spec.key}
                      </span>
                      <span
                        style={{
                          fontWeight: "700",
                          color: "#0f172a",
                          textAlign: "right",
                        }}
                      >
                        {spec.value && spec.value.trim() !== ""
                          ? `${spec.value}${(spec.key?.trim() === "Weight/Coil" ||
                            spec.key?.trim() === "Weight / Coil" ||
                            spec.key?.trim() === "Net Weight/Box") &&
                            !String(spec.value).toLowerCase().includes("kg")
                            ? " kg"
                            : ""
                          }`
                          : "-"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Brand Spotlight Section */}
          {data?.brand_id && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                borderTop: "1px solid #e2e8f0",
                paddingTop: "28px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  borderBottom: "2px solid #cbd5e1",
                  paddingBottom: "12px",
                }}
              >
                <MdVerifiedUser size={18} style={{ color: "#0f172a" }} />
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "800",
                    color: "#0f172a",
                    margin: 0,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Brand Spotlight
                </h3>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "24px",
                  backgroundColor: "#f8fafc",
                  padding: "24px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  flexWrap: "wrap",
                  textAlign: "left",
                }}
              >
                {data.brand_id?.logo && (
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      backgroundColor: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "8px",
                    }}
                  >
                    <img
                      src={data.brand_id.logo}
                      alt={data.brand_id.name}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: "240px" }}>
                  <h4
                    style={{
                      fontSize: "20px",
                      fontWeight: "900",
                      color: "#0f172a",
                      margin: "0 0 6px 0",
                    }}
                  >
                    {data.brand_id?.name}
                  </h4>
                  <p
                    style={{
                      fontSize: "14.5px",
                      color: "#475569",
                      margin: 0,
                      lineHeight: "1.5",
                    }}
                  >
                    {data.brand_id?.description ||
                      "Quality products from a brand you can trust."}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* SECTION 1: SIMILAR PRODUCTS (ARROW/CAROUSEL DESIGN) */}
        {similarProducts.length > 0 && (
          <div className="similar-products-section">
            <div className="suggested-header">
              <div className="header-title-container">
                <h2>Similar Products</h2>
              </div>
              <div className="header-actions">
                <button
                  className="suggested-btn"
                  aria-label="Previous"
                  onClick={() => scroll("left", "similar")}
                >
                  <BiChevronLeft fontSize="24px" />
                </button>
                <button
                  className="suggested-btn"
                  aria-label="Next"
                  onClick={() => scroll("right", "similar")}
                >
                  <BiChevronRight fontSize="24px" />
                </button>
              </div>
            </div>

            <div
              ref={similarScrollRef}
              className="similar-products-scroll-container hide-scrollbar"
            >
              {loadingSimilar ? (
                Array(4)
                  .fill(0)
                  .map((_, i) => (
                    <div
                      key={i}
                      className="liked-product-card-wrapper"
                      style={{
                        minWidth: "260px",
                        height: "350px",
                        backgroundColor: "#f1f3f6",
                        animation:
                          "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                      }}
                    ></div>
                  ))
              ) : (
                <div className="product-listing-grid">
                  {similarProducts.map((p: any) => {
                    const pImage =
                      p.images && p.images.length > 0
                        ? typeof p.images[0] === "string"
                          ? p.images[0]
                          : (p.images[0] as any)?.url || ""
                        : "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&q=80";
                    const pDiscount =
                      p.mrp > p.price
                        ? Math.round(((p.mrp - p.price) / p.mrp) * 100)
                        : 0;
                    return (
                      <div
                        key={p._id || p.id}
                        className="liked-product-card-wrapper"
                      >
                        <ProductCard
                          key={p._id}
                          _id={p._id}
                          image={pImage}
                          name={p.name}
                          price={p.price}
                          originalPrice={p.mrp}
                          off={pDiscount > 0 ? `${pDiscount}%` : undefined}
                          star={4.5}
                          ratingCount={"100"}
                          tag={
                            (p.brand_id as any)?.name ||
                            (p.brand as any)?.name ||
                            "HoneyCool"
                          }
                          images={p.images as any}
                          is_featured={p.is_featured}
                          is_new={p.is_new}
                          isHotDeal={p.stock > 0 && p.stock < 10}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {apiReviews.length > 0 && (
          <div className="reviews-faq-container" ref={reviewsSectionRef}>
          {/* TITLE BANNER */}
          <div style={{ padding: "10px 0 24px", textAlign: "left" }}>
            <h2
              className="happy-subscribers-heading"
              style={{
                fontSize: "28px",
                fontWeight: "800",
                color: "#2d3748",
                margin: 0,
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <span style={{ color: "#43c8ca" }}>Happy Buyers</span>
              <span
                className="heading-line"
                style={{
                  display: "inline-block",
                  width: "40px",
                  height: "3px",
                  backgroundColor: "#43c8ca",
                  marginLeft: "8px",
                  verticalAlign: "middle",
                }}
              ></span>
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#718096",
                margin: "8px 0 0 0",
                fontWeight: "500",
              }}
            >
              Here's what they have to say about their HoneyCool experience.
            </p>
          </div>

          {/* THREE-COLUMN SPLIT LAYOUT */}
          <div
            ref={reviewsSectionRef}
            className="reviews-faq-split-container"
            style={{
              display: "flex",
              gap: "24px",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            {/* COLUMN 1: STICKY REVIEWS SUMMARY (FAR LEFT) */}
            <div
              className="reviews-summary-column"
              style={{
                width: "28%",
                position: "sticky",
                top: "100px",
                zIndex: 10,
              }}
            >
              <div
                className="reviews-summary"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "0px",
                  padding: "24px",
                  border: "1px solid #f1f5f9",
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <h4
                  style={{
                    fontSize: "15px",
                    fontWeight: 900,
                    color: "#1e293b",
                    margin: 0,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  Reviews Summary
                </h4>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "16px" }}
                >
                  <div
                    style={{
                      fontSize: "48px",
                      fontWeight: "900",
                      color: "#282c3f",
                      lineHeight: "1",
                    }}
                  >
                    {displaySummary?.avg || 5.0}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "3px",
                        color: "#ffc107",
                        fontSize: "14px",
                      }}
                    >
                      {Array(Math.round(displaySummary?.avg || 5)).fill(0).map((_, i) => (
                        <FaStar key={i} />
                      ))}
                    </div>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#64748b",
                        fontWeight: "600",
                      }}
                    >
                      {displaySummary?.total || 0} {(displaySummary?.total || 0) === 1 ? 'review' : 'reviews'}
                    </span>
                  </div>
                </div>

                <div
                  className="bars-container"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {(displaySummary?.bars || []).map((item: any) => (
                    <div
                      key={item.s}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#64748b",
                          minWidth: "40px",
                        }}
                      >
                        {item.s} Star
                      </span>
                      <div
                        style={{
                          flex: 1,
                          height: "6px",
                          backgroundColor: "#f1f5f9",
                          borderRadius: "0px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${item.p}%`,
                            height: "100%",
                            backgroundColor: item.color,
                            borderRadius: "0px",
                          }}
                        ></div>
                      </div>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#1e293b",
                          minWidth: "30px",
                          textAlign: "right",
                        }}
                      >
                        {item.p}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* COLUMN 2: REVIEWS GRID LIST (MIDDLE) */}
            <div
              className="reviews-middle-column"
              style={{
                width: "44%",
                display: "flex",
                flexDirection: "column",
                gap: "24px",
              }}
            >
              {/* MASONRY-STYLE REVIEWS GRID */}
              <div
                className="reviews-grid-masonry"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  width: "100%",
                }}
              >
                {(expandedReviews ? displayReviews : displayReviews.slice(0, 4)).map(
                  (item: any) => (
                    <div
                      key={item.id}
                      className="review-card-new"
                      style={{
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        padding: "20px",
                        border: "1px solid #e2e8f0",
                        transition: "all 0.3s ease",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: "800",
                              color: "#1e293b",
                              fontSize: "14px",
                            }}
                          >
                            {item.user}
                          </span>
                          <div
                            style={{
                              display: "flex",
                              gap: "2px",
                              color: "#ffc107",
                            }}
                          >
                            {Array(item.rating || 5).fill(0).map((_, idx) => (
                              <FaStar key={idx} size={11} />
                            ))}
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#a0aec0",
                            fontWeight: "600",
                          }}
                        >
                          {item.date}
                        </span>
                      </div>
                      <p
                        style={{
                          margin: 0,
                          color: "#4a5568",
                          fontSize: "13px",
                          lineHeight: "1.6",
                          fontWeight: "500",
                        }}
                      >
                        {item.comment}
                      </p>
                      {item.images && item.images.length > 0 && (
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
                          {item.images.map((img: any, imgIdx: number) => (
                            <img
                              key={imgIdx}
                              src={typeof img === "string" ? img : img.url}
                              alt={`Product review photo ${imgIdx + 1}`}
                              style={{ width: "56px", height: "56px", borderRadius: "8px", objectFit: "cover", border: "1px solid #e2e8f0" }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ),
                )}
              </div>

              {/* VIEW ALL REVIEWS BUTTON */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "8px",
                }}
              >
                <button
                  onClick={() => setIsReviewsDrawerOpen(true)}
                  style={{
                    backgroundColor: "transparent",
                    color: "#43c8ca",
                    border: "1.5px solid #43c8ca",
                    padding: "10px 24px",
                    fontWeight: "700",
                    fontSize: "12px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    borderRadius: "0",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  className="view-all-reviews-btn"
                >
                  View all reviews <BiChevronRight size={18} />
                </button>
              </div>

              {/* WRITE A REVIEW TRIGGER BUTTON */}
              {/* <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                            <button
                                onClick={() => setShowReviewPopup(true)}
                                style={{
                                    backgroundColor: '#282c3f',
                                    color: '#fff',
                                    padding: '14px 36px',
                                    fontWeight: '900',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    borderRadius: '24px',
                                    border: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxShadow: '0 4px 12px rgba(40, 44, 63, 0.25)'
                                }}
                                className="write-review-trigger-btn"
                            >
                                <FaStar /> Write a Review
                            </button>
                        </div> */}
            </div>

            {/* COLUMN 3: STICKY FAQ SECTION (FAR RIGHT) */}
            <div
              className="faqs-right-column"
              style={{
                width: "28%",
                position: "sticky",
                top: "100px",
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <div
                className="faqs-section"
                style={{
                  border: "1px solid #f1f5f9",
                  borderRadius: "0px",
                  padding: "24px",
                  backgroundColor: "#fff",
                }}
              >
                <div
                  className="faqs-title-wrapper"
                  style={{
                    marginBottom: "1.5rem",
                    borderBottom: "1px solid #f1f5f9",
                    paddingBottom: "12px",
                  }}
                >
                  <span
                    className="faqs-title"
                    style={{
                      fontSize: "15px",
                      fontWeight: 900,
                      color: "#1e293b",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                    }}
                  >
                    Product FAQs
                  </span>
                </div>
                <div
                  className="faq-list"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {faqs.map((faq, index) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #f1f5f9",
                        borderRadius: "0px",
                        overflow: "hidden",
                        backgroundColor: "#fff",
                      }}
                    >
                      <button
                        style={{
                          width: "100%",
                          padding: "16px 20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          border: "none",
                          backgroundColor: "transparent",
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                        onClick={() => toggleFaq(index)}
                      >
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "700",
                            color: "#1e293b",
                          }}
                        >
                          {faq.question}
                        </span>
                        <div style={{ color: "#64748b" }}>
                          {activeFaq === index ? (
                            <FaMinus size={10} />
                          ) : (
                            <FaPlus size={10} />
                          )}
                        </div>
                      </button>
                      <AnimatePresence>
                        {activeFaq === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            style={{ overflow: "hidden" }}
                          >
                            <div
                              style={{
                                padding: "0 20px 16px",
                                color: "#64748b",
                                fontSize: "12px",
                                lineHeight: "1.6",
                              }}
                            >
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* STICKY WRITE A REVIEW POPUP MODAL */}
      {/* <AnimatePresence>
                {showReviewPopup && (
                    <motion.div
                        initial={{ opacity: 0, y: 100, scale: 0.8, x: 50 }}
                        animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
                        exit={{ opacity: 0, y: 100, scale: 0.8, x: 50 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        onMouseEnter={() => {
                            if (reviewTimeoutRef.current) {
                                clearTimeout(reviewTimeoutRef.current);
                            }
                        }}
                        style={{
                            position: 'fixed',
                            bottom: '24px',
                            right: '24px',
                            width: '380px',
                            backgroundColor: '#282c3f',
                            borderRadius: '12px',
                            padding: '28px',
                            color: '#fff',
                            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                            zIndex: 99999,
                            overflow: 'hidden',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            textAlign: 'left'
                        }}
                        className="sticky-review-popup"
                    >
                        <button
                            onClick={() => {
                                setShowReviewPopup(false);
                                setHasClosedReviewPopup(true);
                            }}
                            style={{
                                position: 'absolute',
                                top: '16px',
                                right: '16px',
                                background: 'transparent',
                                border: 'none',
                                color: 'rgba(255, 255, 255, 0.7)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '4px',
                                borderRadius: '50%',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                        >
                            <MdClose size={18} />
                        </button>

                        <div style={{ position: 'relative', zIndex: 2 }}>
                            <h4 style={{ fontSize: '18px', fontWeight: '900', marginBottom: '8px', color: '#fff' }}>Happy with your purchase?</h4>
                            <p style={{ fontSize: '13px', opacity: 0.8, marginBottom: '20px', lineHeight: '1.5' }}>Share your thoughts and help other customers make better choices.</p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <FaStar key={star} style={{ fontSize: '22px', color: star <= 4 ? '#ffc107' : 'rgba(255,255,255,0.2)', cursor: 'pointer' }} />
                                    ))}
                                </div>
                                <textarea
                                    placeholder="Write your review here..."
                                    onFocus={() => {
                                        if (reviewTimeoutRef.current) {
                                            clearTimeout(reviewTimeoutRef.current);
                                        }
                                    }}
                                    style={{ width: '100%', padding: '16px', borderRadius: '8px', border: 'none', minHeight: '100px', backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '13px', outline: 'none', backdropFilter: 'blur(10px)', resize: 'none' }}
                                ></textarea>
                                <button
                                    style={{
                                        alignSelf: 'flex-start',
                                        backgroundColor: '#fff',
                                        color: '#282c3f',
                                        padding: '12px 32px',
                                        borderRadius: '24px',
                                        fontWeight: '900',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
                                    }}
                                    onClick={() => {
                                        toast.success("Thank you for your valuable feedback!");
                                        setShowReviewPopup(false);
                                        setHasClosedReviewPopup(true);
                                    }}
                                >
                                    Post Review
                                </button>
                            </div>
                        </div>
                        <div style={{ position: 'absolute', right: '-40px', bottom: '-40px', width: '180px', height: '180px', backgroundColor: '#43c8ca', borderRadius: '50%', opacity: 0.2, filter: 'blur(50px)' }}></div>
                    </motion.div>
                )}
            </AnimatePresence> */}

      {/* RIGHT SIDE REVIEWS DRAWER */}
      <AnimatePresence>
        {isReviewsDrawerOpen && (
          <>
            {/* Backdrop overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsReviewsDrawerOpen(false)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.4)",
                backdropFilter: "blur(4px)",
                zIndex: 999998,
              }}
            />

            {/* Drawer body */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.35, ease: "easeOut" }}
              style={{
                position: "fixed",
                top: 0,
                right: 0,
                width: "30vw",
                minWidth: "420px",
                maxWidth: "550px",
                height: "100%",
                backgroundColor: "#fff",
                boxShadow: "-10px 0 30px rgba(0, 0, 0, 0.15)",
                zIndex: 999999,
                display: "flex",
                flexDirection: "column",
                textAlign: "left",
              }}
              className="reviews-drawer"
            >
              {/* Drawer Header */}
              <div
                style={{
                  padding: "24px",
                  borderBottom: "1px solid #f1f5f9",
                  position: "relative",
                }}
              >
                <button
                  onClick={() => setIsReviewsDrawerOpen(false)}
                  style={{
                    position: "absolute",
                    top: "24px",
                    right: "24px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    color: "#64748b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(0,0,0,0.02)",
                  }}
                >
                  <MdClose size={20} />
                </button>
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: "800",
                    color: "#1e293b",
                    margin: "0 0 12px 0",
                  }}
                >
                  Reviews & ratings
                </h3>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "16px" }}
                >
                  <span
                    style={{
                      fontSize: "40px",
                      fontWeight: "900",
                      color: "#282c3f",
                      lineHeight: 1,
                    }}
                  >
                    {displaySummary ? displaySummary.avg : "5.0"}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "3px",
                        color: "#ffc107",
                        fontSize: "13px",
                      }}
                    >
                      {Array(Math.round(displaySummary ? displaySummary.avg : 5)).fill(0).map((_, i) => (
                        <FaStar key={i} />
                      ))}
                    </div>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        fontWeight: "600",
                      }}
                    >
                      Based on {displaySummary ? displaySummary.total : displayReviews.length} reviews
                    </span>
                  </div>
                </div>
              </div>

              {/* Scrollable Reviews List */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  backgroundColor: "#f8fafc",
                }}
                className="custom-scrollbar"
              >
                {displayReviews.map((item: any) => (
                  <div
                    key={item.id}
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "12px",
                      padding: "20px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: "800",
                            color: "#1e293b",
                            fontSize: "14px",
                          }}
                        >
                          {item.user}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            gap: "2px",
                            color: "#ffc107",
                          }}
                        >
                          {Array(item.rating || 5).fill(0).map((_, idx) => (
                            <FaStar key={idx} size={11} />
                          ))}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: "11px",
                          color: "#a0aec0",
                          fontWeight: "600",
                        }}
                      >
                        {item.date}
                      </span>
                    </div>
                    <p
                      style={{
                        margin: 0,
                        color: "#4a5568",
                        fontSize: "13px",
                        lineHeight: "1.6",
                        fontWeight: "500",
                      }}
                    >
                      {item.comment}
                    </p>
                    {item.images && item.images.length > 0 && (
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
                        {item.images.map((img: any, imgIdx: number) => (
                          <img
                            key={imgIdx}
                            src={typeof img === "string" ? img : img.url}
                            alt={`Review photo ${imgIdx + 1}`}
                            style={{ width: "56px", height: "56px", borderRadius: "8px", objectFit: "cover", border: "1px solid #e2e8f0" }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Drawer Footer */}
              <div
                style={{
                  padding: "20px 24px",
                  borderTop: "1px solid #f1f5f9",
                  backgroundColor: "#fff",
                }}
              >
                <button
                  onClick={() => setIsReviewsDrawerOpen(false)}
                  style={{
                    width: "100%",
                    backgroundColor: "#43c8ca",
                    color: "#fff",
                    border: "none",
                    padding: "14px 20px",
                    borderRadius: "8px",
                    fontWeight: "800",
                    fontSize: "13px",
                    cursor: "pointer",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    textAlign: "center",
                    boxShadow: "0 4px 12px rgba(67, 200, 202, 0.25)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#35b3b5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#43c8ca")
                  }
                >
                  Go Back
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <div ref={footerRef}>
        <Footer />
      </div>
    </motion.div>
  );
};

export default DynamicProductDetails;
